import React, { useEffect, useState, useCallback, useMemo, memo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "@mui/material/TablePagination";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import { green, red } from "@mui/material/colors";
import BorderColorIcon from "@mui/icons-material/BorderColor";
// Filled circle variants -- plain Check/Close are thin line-strokes and read
// noticeably lighter than the solid BorderColorIcon pencil; these carry
// comparable visual weight.
import CheckIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Cancel";
import {
  TextField,
  Grid,
  Autocomplete,
  Tooltip,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import SearchIcon from "@mui/icons-material/Search";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import { IndeterminateCheckBoxOutlined } from "@mui/icons-material";
import DragIndicatroIcon from "@mui/icons-material/DragIndicator";
import SnackBar from "components/SnackBar/SnackBar";

const createData = (headCells, source) => {
  const getFormatedRow = (headCells, item) => {
    const row = headCells.map((headCell) => ({
      ...item,
      [headCell.fieldName]: item[headCell.fieldName],
      edit: false,
    }));

    return Object.assign({}, ...row);
  };

  const data = source.map((item) => {
    return getFormatedRow(headCells, item);
  });

  return data;
};

function descendingComparator(a, b, orderBy) {
  if (typeof a[orderBy] === "number" && typeof (b[orderBy] === "number")) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
  } else {
    if (
      b[orderBy].toString().toLowerCase() < a[orderBy].toString().toLowerCase()
    ) {
      return -1;
    }
    if (
      b[orderBy].toString().toLowerCase() > a[orderBy].toString().toLowerCase()
    ) {
      return 1;
    }
  }

  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const TableSortLabelStyle = {
  "&.MuiTableSortLabel-root": {
    color: "#3F5765",
    fontWeight: "bold",
    fontFamily: "Urbanist",
  },
  "&.Mui-active": {
    color: "#3F5765",
  },
  "& .MuiTableSortLabel-icon": {
    color: "#3299FF !important",
  },
};

function EnhancedTableHead(props) {
  const {
    order,
    orderBy,
    onRequestSort,
    headCells,
    isReadOnly,
    dragEnabled,
    filterableFields = [],
    filters = {},
    onFilterChange,
  } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {dragEnabled && <TableCell key="drag-header" />}

        {!isReadOnly && !dragEnabled && (
          <TableCell
            key={"controls"}
            align="center"
            sx={{
              fontWeight: "bold",
              fontFamily: "Urbanist",
              color: "#3F5765",
            }}
          >
            Edit
          </TableCell>
        )}

        {headCells.map((headCell) =>
          headCell?.control === "remove" || headCell?.control === "applyToAll" ? (
            <TableCell
              key={headCell.fieldName}
              align="center"
              sx={{
                fontWeight: "bold",
                fontFamily: "Urbanist",
                whiteSpace: "nowrap",
                color: "#3F5765",
              }}
            >
              {headCell?.label}
            </TableCell>
          ) : (
            <TableCell
              key={headCell.fieldName}
              align={headCell?.align ?? "center"}
              sortDirection={
                !dragEnabled && orderBy === headCell.fieldName ? order : false
              }
              sx={{
                fontWeight: "bold",
                fontFamily: "Urbanist",
                color: "#3F5765",
              }}
            >
              {dragEnabled ? (
                // Sorting is inert while dragging, so show a plain label instead of a dead control.
                headCell.label
              ) : (
                <TableSortLabel
                  active={orderBy === headCell.fieldName}
                  direction={orderBy === headCell.fieldName ? order : "asc"}
                  onClick={createSortHandler(headCell.fieldName)}
                  sx={TableSortLabelStyle}
                >
                  {headCell.label}
                  {orderBy === headCell.fieldName ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </TableSortLabel>
              )}
            </TableCell>
          )
        )}
      </TableRow>

      {/* Filter row -- hidden while drag-to-reorder is on. */}
      {!dragEnabled && filterableFields.length > 0 && (
        <TableRow>
          {!isReadOnly && <TableCell key="filter-controls-placeholder" />}
          {headCells.map((headCell) => {
            if (!filterableFields.includes(headCell.fieldName)) {
              return <TableCell key={`filter-${headCell.fieldName}`} />;
            }

            const value = filters[headCell.fieldName] ?? "";
            const handleChange = (event) =>
              onFilterChange?.(headCell.fieldName, event.target.value);

            if (headCell.control === "active") {
              return (
                <TableCell key={`filter-${headCell.fieldName}`}>
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    value={value}
                    onChange={handleChange}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </TableCell>
              );
            }

            if (headCell.control === "autoComplete") {
              const options = headCell.dataSource ?? [];
              return (
                <TableCell key={`filter-${headCell.fieldName}`}>
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    value={value}
                    onChange={handleChange}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {options.map((option) => (
                      <MenuItem
                        key={option[headCell.fieldName]}
                        value={option[headCell.fieldName]}
                      >
                        {option[headCell.displayFieldName]}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
              );
            }

            return (
              <TableCell key={`filter-${headCell.fieldName}`}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Filter..."
                  value={value}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "#9AA5B1" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
            );
          })}
        </TableRow>
      )}
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
  headCells: PropTypes.array,
  isReadOnly: PropTypes.bool,
};

function EditableLabel(props) {
  const { row, headCell, onChangeEvent } = props;

  if (!row || !headCell || !headCell.fieldName) {
    return <></>;
  }
  const value = row[headCell.fieldName] ?? "";

  return row?.edit === false ? (
    <>{value}</>
  ) : (
    <TextField
      fullWidth
      size="small"
      variant="outlined"
      sx={{
        height: "39px",
        color: "#74747438",
      }}
      id={headCell?.fieldName}
      placeholder={headCell?.label}
      value={value}
      onChange={(event) => onChangeEvent?.(event, row?.id)}
    />
  );
}

const getSelectedValue = (dataSource, key, findValue) => {
  return dataSource.find((item) => item[key] === findValue);
};

const getLabelValue = (rowId, headCell) => {
  const match = headCell?.dataSource?.find(
    (item) => item?.[headCell?.fieldName] === rowId
  );
  return match?.[headCell?.displayFieldName] ?? "";
};

function EditableAutoComplete(props) {
  const { row, headCell, handleChange } = props;

  // Optional per-row editability check; defaults to true if unset.
  const isColumnEditableForRow =
    typeof headCell?.isEditableForRow === "function"
      ? headCell.isEditableForRow(row)
      : true;

  return row?.edit === false || !isColumnEditableForRow ? (
    getLabelValue(row[headCell?.fieldName], headCell)
  ) : (
    <Autocomplete
      disablePortal
      value={getSelectedValue(
        headCell?.dataSource,
        headCell?.fieldName,
        row[headCell?.fieldName]
      )}
      id={headCell?.fieldName}
      options={headCell?.editableOptions ?? headCell?.dataSource}
      getOptionLabel={(item) => `${item[headCell?.displayFieldName]}`}
      sx={{ width: "100%", height: "39px" }}
      onChange={(event, value) =>
        handleChange(event, value, headCell?.fieldName, row?.id)
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          aria-label={headCell?.label}
        />
      )}
    />
  );
}

const prepareRowForSave = (row) => {
  let data = row;
  delete data?.edit;
  return data;
};

// ============================================================================
// EXTRACTED COMPONENTS FOR TABLE CELLS
// ============================================================================

// Shared fixed cell height so rows don't jump size between view and edit mode.
const editableCellSx = {
  height: 52,
  paddingTop: "6px",
  paddingBottom: "6px",
};

const LabelCell = ({ headCell, row, isReadOnly, handleInputChange }) => (
  <TableCell
    key={headCell?.fieldName}
    align={headCell?.align ?? "center"}
    sx={{
      ...editableCellSx,
      color: "#3F5765",
    }}
  >
    {headCell?.editable === false ? (
      row[headCell?.fieldName]
    ) : (
      <EditableLabel
        row={row}
        headCell={headCell}
        onChangeEvent={handleInputChange}
      />
    )}
  </TableCell>
);

const RemoveCell = ({ headCell, row, isReadOnly, handleRemoveClick }) => (
  <TableCell padding="checkbox" key={headCell?.fieldName} align="center">
    <IconButton
      disabled={isReadOnly}
      onClick={() => handleRemoveClick(row?.id)}
    >
      <IndeterminateCheckBoxOutlined
        fontSize="medium"
        sx={{
          color: isReadOnly ? "inherit" : red[600],
          cursor: isReadOnly ? "not-allowed" : "pointer",
          borderRadius: 3,
        }}
      />
    </IconButton>
  </TableCell>
);

const ActiveCell = ({ headCell, row, handleActiveCheck }) => (
  <TableCell padding="checkbox" key={headCell?.fieldName} align="center">
    <Checkbox
      id={row?.id?.toString()}
      checked={row[headCell?.fieldName]}
      onChange={(event) => handleActiveCheck(event, headCell?.fieldName)}
      sx={{
        color: red[800],
        "&.Mui-checked": {
          color: green[600],
        },
      }}
    />
  </TableCell>
);

const AutoCompleteCell = ({ headCell, row, handleAutoCompleteChange }) => (
  <TableCell
    key={headCell?.fieldName}
    align={headCell?.align ?? "center"}
    sx={{ ...editableCellSx, color: "#3F5765" }}
  >
    {headCell?.editable === false ? (
      row[headCell?.fieldName]
    ) : (
      <EditableAutoComplete
        row={row}
        headCell={headCell}
        handleChange={handleAutoCompleteChange}
      />
    )}
  </TableCell>
);

const ApplyToAllCell = ({ headCell, row, onApplyToAll }) => (
  <TableCell padding="checkbox" key={headCell?.fieldName} align="center">
    <Tooltip title="Apply to all" placement="top">
      <IconButton
        size="small"
        disableRipple
        onClick={() => onApplyToAll && onApplyToAll(row)}
        aria-label="Apply to all"
        sx={{
          color: "#3299FF",
          "&:hover": { backgroundColor: "#EBF5FF" },
          borderRadius: "6px",
        }}
      >
        <PlaylistAddIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </TableCell>
);

// ============================================================================
// HELPER COMPONENT FOR EDIT CONTROLS
// ============================================================================

const EditControls = ({
  row,
  handleEditClick,
  onSaveClick,
  handleCloseClick,
}) => {
  if (row?.edit === true) {
    return (
      <Grid container>
        <Grid item xs={4.5}>
          <IconButton
            size="small"
            disableRipple
            onClick={() => {
              onSaveClick(row);
            }}
            aria-label="Save"
            sx={{
              // Green = confirm, distinct from Edit/Apply-to-All's blue.
              color: green[600],
              borderRadius: "6px",
              "&:hover": { backgroundColor: green[50] },
            }}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
        </Grid>
        <Grid item xs={3}></Grid>
        <Grid item xs={4.5}>
          <IconButton
            size="small"
            disableRipple
            onClick={() => handleCloseClick()}
            aria-label="Cancel"
            sx={{
              // TTEC brand orange -- distinct from Save's green, without
              // reusing red (reserved for the destructive Remove action).
              color: "#EC6517",
              borderRadius: "6px",
              marginLeft: 1,
              "&:hover": { backgroundColor: "#FDEEE3" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={4.5}>
        <IconButton
          size="small"
          disableRipple
          onClick={() => handleEditClick(row?.id)}
          aria-label="Edit"
          sx={{
            color: "#3299FF",
            borderRadius: "6px",
            "&:hover": { backgroundColor: "#EBF5FF" },
          }}
        >
          <BorderColorIcon fontSize="small" />
        </IconButton>
      </Grid>
    </Grid>
  );
};

// ============================================================================
// HELPER COMPONENT FOR TABLE CELLS BASED ON CONTROL TYPE
// ============================================================================

const TableCellByControl = memo(({
  headCell,
  row,
  isReadOnly,
  handleInputChange,
  handleRemoveClick,
  handleActiveCheck,
  handleAutoCompleteChange,
  onApplyToAll,
}) => {
  switch (headCell?.control) {
    case "label":
      return (
        <LabelCell
          headCell={headCell}
          row={row}
          isReadOnly={isReadOnly}
          handleInputChange={handleInputChange}
        />
      );

    case "remove":
      return (
        <RemoveCell
          headCell={headCell}
          row={row}
          isReadOnly={isReadOnly}
          handleRemoveClick={handleRemoveClick}
        />
      );

    case "active":
      return (
        <ActiveCell
          headCell={headCell}
          row={row}
          handleActiveCheck={handleActiveCheck}
        />
      );

    case "autoComplete":
      return (
        <AutoCompleteCell
          headCell={headCell}
          row={row}
          handleAutoCompleteChange={handleAutoCompleteChange}
        />
      );

    case "applyToAll":
      return (
        <ApplyToAllCell
          headCell={headCell}
          row={row}
          onApplyToAll={onApplyToAll}
        />
      );

    default:
      return <></>;
  }
});

// ============================================================================
// DRAGGABLE TABLE ROW COMPONENT
// ============================================================================

const DraggableTableRow = memo(({
  row,
  index,
  dragEnabled,
  headCells,
  isReadOnly,
  handleInputChange,
  handleRemoveClick,
  handleActiveCheck,
  handleAutoCompleteChange,
  handleEditClick,
  requestSave,
  handleCloseClick,
  onApplyToAll,
}) => {
  return (
    <Draggable
      key={row.id?.toString()}
      draggableId={row.id?.toString()}
      index={index}
      isDragDisabled={!dragEnabled || row?.edit}
    >
      {(provided, snapshot) => (
        <TableRow
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...(dragEnabled ? provided.dragHandleProps : {})}
          hover
          tabIndex={-1}
          sx={{
            cursor: dragEnabled ? "grab" : "pointer",
            backgroundColor: snapshot.isDragging ? "#f0f0f0" : "inherit",
          }}
        >
          {dragEnabled && (
            <TableCell key="drag-icon">
              <DragIndicatroIcon sx={{ color: "#ccc" }} />
            </TableCell>
          )}

          {!isReadOnly && !dragEnabled && (
            <TableCell padding="checkbox" align="center">
              <EditControls
                row={row}
                handleEditClick={handleEditClick}
                onSaveClick={requestSave}
                handleCloseClick={handleCloseClick}
              />
            </TableCell>
          )}

          {headCells.map((headCell) => (
            <TableCellByControl
              key={headCell?.fieldName}
              headCell={headCell}
              row={row}
              isReadOnly={isReadOnly}
              handleInputChange={handleInputChange}
              handleRemoveClick={handleRemoveClick}
              handleActiveCheck={handleActiveCheck}
              handleAutoCompleteChange={handleAutoCompleteChange}
              onApplyToAll={onApplyToAll}
            />
          ))}
        </TableRow>
      )}
    </Draggable>
  );
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================

EnhancedDataGrid.propTypes = {
  headCells: PropTypes.array.isRequired,
  dataSource: PropTypes.array.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleReorder: PropTypes.func.isRequired,
  handleRemove: PropTypes.func,
  isReadOnly: PropTypes.bool,
  setVisibleRows: PropTypes.func.isRequired,
  dragEnabled: PropTypes.bool,
  onApplyToAll: PropTypes.func,
  confirmChangeFields: PropTypes.arrayOf(PropTypes.string),
  getConfirmMessage: PropTypes.func,
  filterableFields: PropTypes.arrayOf(PropTypes.string),
  onFilterActiveChange: PropTypes.func,
};

export default function EnhancedDataGrid(props) {
  const {
    headCells,
    dataSource,
    handleSave,
    isReadOnly,
    setVisibleRows,
    dragEnabled = false,
    onApplyToAll,
    // Optional: fieldNames to show a filter control for. Mutually exclusive with drag-to-reorder.
    filterableFields = [],
    onFilterActiveChange,
    // Optional: fieldNames that, if changed, prompt a confirm dialog before saving.
    confirmChangeFields,
    // (row, originalRow, changedField) => string, used with confirmChangeFields.
    getConfirmMessage,
  } = props;
  const handleRemove = props?.handleRemove;
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState(headCells[0]?.fieldName);
  // rowsPerPage of -1 means "All" (matches MUI TablePagination's convention).
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = React.useState([]);
  const dense = false;
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [rowToRemove, setRowToRemove] = React.useState(0);
  const [confirmSaveDialog, setConfirmSaveDialog] = React.useState({
    open: false,
    row: null,
    message: "",
  });
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  // ── Column filters ──────────────────────────────────────────────────────
  // Keyed by fieldName; empty/null means "no filter" for that field.
  const [filters, setFilters] = useState({});

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((value) => value !== "" && value != null),
    [filters],
  );

  // Lets the parent disable its own Reorder toggle while a filter is active.
  useEffect(() => {
    if (typeof onFilterActiveChange === "function") {
      onFilterActiveChange(hasActiveFilters);
    }
  }, [hasActiveFilters, onFilterActiveChange]);

  const handleFilterChange = (fieldName, value) => {
    setFilters((prev) => ({ ...prev, [fieldName]: value }));
  };

  const filteredRows = useMemo(() => {
    // Filters are suspended (not cleared) while dragging, so every row stays
    // visible for reordering and prior filters reapply once it ends.
    if (dragEnabled || !hasActiveFilters) return rows;
    return rows.filter((row) => {
      // While a row is being edited, match against its last-saved value
      // instead of the live, unsaved edit -- otherwise editing the exact
      // field a filter is applied to makes the row vanish mid-edit, well
      // before Save. Mirrors the same protection applied to sorting below.
      const savedRow = row?.edit
        ? (allRows.find((r) => r.id === row.id) ?? row)
        : row;
      return Object.entries(filters).every(([fieldName, value]) => {
        if (value === "" || value == null) return true;
        const headCell = headCells.find((hc) => hc.fieldName === fieldName);
        const cellValue = savedRow[fieldName];
        if (headCell?.control === "active") {
          // value is the string "true"/"false" from the filter Select
          return String(Boolean(cellValue)) === value;
        }
        if (headCell?.control === "autoComplete") {
          return String(cellValue) === String(value);
        }
        // Plain text field: case-insensitive substring match
        return String(cellValue ?? "")
          .toLowerCase()
          .includes(String(value).toLowerCase());
      });
    });
  }, [rows, filters, hasActiveFilters, headCells, dragEnabled, allRows]);

  useEffect(() => {
    setRows(createData(headCells, dataSource));
    setAllRows(createData(headCells, dataSource));
  }, [headCells, dataSource]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.name);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const visibleRows = React.useMemo(() => {
    if (dragEnabled) return filteredRows;

    // An editing row keeps its position until saved, using its last-saved
    // value as the sort key instead of the live, unsaved edit.
    const withSortStandIn = filteredRows.map((row, originalIndex) => {
      let standIn = row;
      if (row?.edit) {
        const original = allRows.find((r) => r.id === row.id);
        if (original && original[orderBy] !== undefined) {
          standIn = { ...row, [orderBy]: original[orderBy] };
        }
      }
      return { ...standIn, __originalIndex: originalIndex };
    });

    const sorted = stableSort(withSortStandIn, getComparator(order, orderBy));
    return sorted.map((item) => filteredRows[item.__originalIndex]);
  }, [order, orderBy, filteredRows, allRows, dragEnabled]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    !dragEnabled && rowsPerPage !== -1
      ? Math.max(0, (1 + page) * rowsPerPage - visibleRows.length)
      : 0;

  // Reset to page 0 whenever the row set's composition changes underneath
  // the current page (sorting, filtering, page size, or entering Reorder
  // Mode) -- otherwise the user could land on a now out-of-range page.
  useEffect(() => {
    setPage(0);
  }, [order, orderBy, filters, rowsPerPage, dragEnabled]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Pagination is suspended while dragging, same as filtering -- reordering
  // needs every row visible and draggable at once.
  const paginatedRows = useMemo(() => {
    if (dragEnabled || rowsPerPage === -1) return visibleRows;
    const start = page * rowsPerPage;
    return visibleRows.slice(start, start + rowsPerPage);
  }, [visibleRows, page, rowsPerPage, dragEnabled]);

  // Drop Apply-to-All while dragging (only reordering makes sense there); Edit is hidden the same way where it's rendered.
  const visibleHeadCells = useMemo(
    () =>
      dragEnabled
        ? headCells.filter((hc) => hc.control !== "applyToAll")
        : headCells,
    [headCells, dragEnabled],
  );

  const handleActiveCheck = (event, key) => {
    let data = rows;

    data = data.map((item) => {
      return {
        ...item,
        ...(item?.id == event.target?.id && { [key]: !item[key] }),
      };
    });
    let row = data.filter((item) => item?.id == event.target?.id);
    row = Object.assign({}, ...row);
    handleSave(row?.id, prepareRowForSave(row));
    setRows(data);
  };

  const handleInputChange = useCallback((event, key) => {
    setRows((prevRows) =>
      prevRows.map((item) =>
        item?.id === key
          ? { ...item, [event.target.id]: event.target.value }
          : item,
      ),
    );
  }, []);

  const handleAutoCompleteChange = useCallback((event, value, key, rowId) => {
    if (!value) return;
    setRows((prevRows) =>
      prevRows.map((item) =>
        item?.id === rowId ? { ...item, [key]: value[key] } : item,
      ),
    );
    // Scope the update to just the changed field, not the whole selected option object.
  }, []);

  // Only rows whose `edit` flag actually changes get a new reference, so
  // DraggableTableRow's React.memo can skip re-rendering the rest.
  const handleEditClick = useCallback(
    (rowId) => {
      const freshTarget = createData(headCells, dataSource).find(
        (item) => item.id === rowId,
      );
      setRows((prevRows) =>
        prevRows.map((item) => {
          if (item.id === rowId) {
            return { ...freshTarget, edit: true };
          }
          return item.edit ? { ...item, edit: false } : item;
        }),
      );
    },
    [headCells, dataSource],
  );

  const handleCloseClick = useCallback(() => {
    setRows((prevRows) =>
      prevRows.map((item) => (item.edit ? { ...item, edit: false } : item)),
    );
  }, []);

  // Declared after handleEditClick/handleCloseClick since they depend on those.
  const commitSave = useCallback(
    (row) => {
      handleEditClick(row?.id);
      handleSave(row?.id, prepareRowForSave(row));
    },
    [handleEditClick, handleSave],
  );

  const requestSave = useCallback(
    (row) => {
      const originalRow = allRows.find((item) => item?.id === row?.id);
      const changedField = confirmChangeFields?.find(
        (field) => originalRow && row?.[field] !== originalRow?.[field],
      );

      if (changedField && typeof getConfirmMessage === "function") {
        setConfirmSaveDialog({
          open: true,
          row,
          message: getConfirmMessage(row, originalRow, changedField),
        });
      } else {
        commitSave(row);
      }
    },
    [allRows, confirmChangeFields, getConfirmMessage, commitSave],
  );

  const handleConfirmSaveConfirm = () => {
    const { row } = confirmSaveDialog;
    setConfirmSaveDialog({ open: false, row: null, message: "" });
    commitSave(row);
  };

  const handleConfirmSaveCancel = () => {
    setConfirmSaveDialog({ open: false, row: null, message: "" });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleRemoveClick = (rowId) => {
    setDialogOpen(true);
    setRowToRemove(rowId);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reordered = Array.from(dataSource);
    const [movedItem] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, movedItem);

    const updatedWithOrder = reordered.map((item, index) => ({
      ...item,
      displayOrder: index + 1,
    }));

    setVisibleRows(updatedWithOrder);
    await props.handleReorder(updatedWithOrder);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box
          sx={{
            ...(dragEnabled && {
              maxHeight: "calc(100vh - 280px)",
              overflowY: "auto",
            }),
          }}
        >
        <TableContainer
          sx={{
            maxHeight: "calc(100vh - 280px)",
            overflowY: dragEnabled ? "visible" : "scroll",
            overflow: dragEnabled ? "visible" : undefined,
          }}
        >
          <Table
            sx={{ minWidth: 750 }}
            size={dense ? "small" : "medium"}
            stickyHeader
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              headCells={visibleHeadCells}
              isReadOnly={isReadOnly}
              dragEnabled={dragEnabled}
              filterableFields={filterableFields}
              filters={filters}
              onFilterChange={handleFilterChange}
            />

            <Droppable droppableId="table-body" ignoreContainerClipping>
              {(provided) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {paginatedRows.map((row, index) => (
                    <DraggableTableRow
                      key={row.id?.toString()}
                      row={row}
                      index={index}
                      dragEnabled={dragEnabled}
                      headCells={visibleHeadCells}
                      isReadOnly={isReadOnly}
                      handleInputChange={handleInputChange}
                      handleRemoveClick={handleRemoveClick}
                      handleActiveCheck={handleActiveCheck}
                      handleAutoCompleteChange={handleAutoCompleteChange}
                      handleEditClick={handleEditClick}
                      requestSave={requestSave}
                      handleCloseClick={handleCloseClick}
                      onApplyToAll={onApplyToAll}
                    />
                  ))}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell
                        colSpan={
                          (!isReadOnly ? 1 : 0) + visibleHeadCells.length
                        }
                      />
                    </TableRow>
                  )}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </TableContainer>
        </Box>
      </DragDropContext>

      {/* Pagination is suspended while dragging -- reordering needs every row visible. */}
      {!dragEnabled && (
        <TablePagination
          rowsPerPageOptions={[
            5,
            10,
            25,
            50,
            { label: "All", value: -1 },
          ]}
          component="div"
          count={visibleRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure , you want to delete this row ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              handleRemove(rowToRemove);
            }}
          >
            Confirm
          </Button>
          <Button onClick={handleDialogClose} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmSaveDialog.open}
        onClose={handleConfirmSaveCancel}
        aria-labelledby="confirm-save-dialog-title"
        aria-describedby="confirm-save-dialog-description"
      >
        <DialogTitle id="confirm-save-dialog-title">Confirm change</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-save-dialog-description">
            {confirmSaveDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmSaveConfirm}>Confirm</Button>
          <Button onClick={handleConfirmSaveCancel} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </Box>
  );
}
